<template>
    <div v-if="canUpdate">
        <settings-heading
            :title="$t('settings.gdpr.title')"
            :description="$t('settings.gdpr.description')"
        />
        <div>
            <toggle
                v-model="form.enable_gdpr"
                class="mb-32"
            >
                {{ $t('settings.gdpr.enable') }}
            </toggle>

            <div v-if="form.enable_gdpr" class="bg-primary-5 border border-primary-5 p-24 mb-32 rounded-6">
                <p class="mb-16 text-primary-80 mr-32 max-w-640">{{ $t('settings.gdpr.data_retention_period_description') }}</p>
                <div class="flex mb-32">
                    <simple-input type="number" v-model="form.data_retention_period" class="max-w-240 mr-12">
                        <template #default>{{ $t('settings.gdpr.data_retention_period') }}</template>
                        <template #after>
                            <p>{{ $t('settings.gdpr.days') }}</p>
                        </template>
                    </simple-input>
                </div>
                <p class="mb-16 text-primary-80 mr-32 max-w-640">{{ $t('settings.gdpr.auto_anonymise_applications_description') }}</p>
                <toggle
                    v-model="form.auto_anonymise_applications"
                >
                    {{ $t('settings.gdpr.auto_anonymise_applications') }}
                </toggle>
            </div>

            <div class="flex justify-end mb-48">
                <simple-button
                    :disabled="!form.isDirty()"
                    display="secondary"
                    icon="save"
                    :loading="form.submitting && !expiringCount"
                    look="primary"
                    @click="submitForm"
                >
                    {{ $t('settings.save') }}
                </simple-button>
            </div>

        </div>

        <popup-modal-confirm
            v-if="expiringCount"
            :message="$t('settings.gdpr.expiring_applications_detected', {expiringCount, period: form.data_retention_period})"
            :confirmText="$t('settings.gdpr.apply_drp')"
            @close="expiringCount = null"
            @confirm="submitForm"
            :working="form.submitting"
            confirm-button="success"
        />
    </div>
    <error-403 v-else></error-403>
</template>

<script>
import { mapState } from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';
import Toggle from '../../components/forms/Toggle.vue';
import Form from '../../etc/forms/Form';
import Error403 from '../../components/ui/Error403.vue';
import Input from '../../components/forms/Input.vue';
import SimpleButton from '../../components/ui/SimpleButton.vue';
import Company from '../../models/Company';
import { normalizeJson } from '../../../shared/helpers';
import * as companies from '../../services/CompanyService';
import PopupModalConfirm from '../../components/ui/PopupModalConfirm.vue';
import confirmLeave from '../../mixins/confirmLeave';
import SettingsHeading from "../../components/ui/SettingsHeading.vue";

export default {
    components: {
        SettingsHeading,
        Toggle,
        'error-403': Error403,
        'simple-input': Input,
        'simple-button': SimpleButton,
        'popup-modal-confirm': PopupModalConfirm,
    },
    mixins: [
        confirmLeave,
    ],
    setup() {
        const mainStore = useMainStore();
        return {mainStore};
    },
    data: function () {
        return {
            form: this.generateForm(),
            canUpdate: this.$can('settings.advanced'),
            expiringCount: null,
        }
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany']),
    },
    methods: {
        generateForm () {
            return new Form([
                {
                    name: 'enable_gdpr',
                    value: !!this.mainStore.currentCompany.data_retention_period,
                },
                {
                    name: 'data_retention_period',
                    value: this.mainStore.currentCompany.data_retention_period || 365,
                },
                {
                    name: 'auto_anonymise_applications',
                    value: this.mainStore.currentCompany.auto_anonymise_applications,
                }
            ]);
        },
        submitForm () {
            this.form.submit(data => {
                return companies.update(this.currentCompany.id, {
                    data: {
                        attributes: {
                            data_retention_period: this.form.enable_gdpr ? this.form.data_retention_period : null,
                            auto_anonymise_applications: this.form.auto_anonymise_applications
                        },
                        ...(this.expiringCount && {expiring_count: this.expiringCount})
                    }});
                }).then(result => {
                    this.expiringCount = null;
                    return useRepo(Company)
                        .whereId(result.id)
                        .update(normalizeJson(result));
                }).catch(error => {
                    if (error.response.status === 422 && error.response.data.expiring_count) {
                        this.expiringCount = error.response.data.expiring_count;
                    } else {
                        throw(error);
                    }
                });
        },
    },
}
</script>
